import { useState } from 'react';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { formatRelative } from 'date-fns';
import InsightsIcon from '@mui/icons-material/Insights';
import ShareIcon from '@mui/icons-material/Share';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import TabPanel from '@/components/tabPanel';
import EntityAccessControl from '../entities/administration/accessControl/accessControl';
import AuditRecords from '../entities/administration/auditRecords/auditRecords';
import AgentUsageStats from './usageStats';

type Props = { agentInstance: any; isOpen: boolean; maxHeight: number };

const AgentInstanceAdministration = ({ agentInstance = {}, isOpen, maxHeight }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const apiContext = useApiContext();
  const confirm = useConfirm();

  const canDelete = hasPermission(`pipeline:agents:delete`, agentInstance?.userAccess);

  const [selectedTab, setSelectedTab] = useState<any>(
    hasPermission(`pipeline:agents:accessControl:view`, agentInstance?.userAccess)
      ? 'accessControl'
      : hasPermission(`pipeline:agents:usageStats:view`, agentInstance?.userAccess)
        ? 'usageStats'
        : 'auditTrail',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const deleteAgentInstance = async () => {
    confirm({ description: 'Are you sure you want to delete this agent instance?' }).then(async () => {
      try {
        setLoading(true);
        await apiContext.deleteAgentInstance(agentInstance.entityDefId, agentInstance.id);
        const from = location.state?.from || '/instances?tab=agentInstances';
        navigate(from);
      } catch (error) {
        setError(error);
      }
      setLoading(false);
    });
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0 }}>
        {error && <ErrorMessage error={error} />}

        {/* Metadata Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.25,
            border: 1,
            borderColor: 'divider',
            borderRadius: '12px',
            mb: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {agentInstance?.name} ({agentInstance?.agentDef?.name})
            </Typography>
            {agentInstance?.createdDate && (
              <Typography variant="caption" color="text.secondary">
                Created: {formatRelative(new Date(agentInstance.createdDate), new Date())} by {agentInstance.createdBy}
              </Typography>
            )}
            {agentInstance?.lastUpdatedDate && (
              <Typography variant="caption" color="text.secondary">
                Updated: {formatRelative(new Date(agentInstance.lastUpdatedDate), new Date())}
              </Typography>
            )}
          </Stack>
          {canDelete && (
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={deleteAgentInstance}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete Instance
            </Button>
          )}
        </Stack>

        {/* Tabs Bar */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="secondary" sx={{ minHeight: 40 }}>
            {hasPermission(`pipeline:agents:accessControl:view`, agentInstance?.userAccess) && (
              <Tab
                value="accessControl"
                icon={<ShareIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'accessControl' ? 700 : 500 }}>
                    Access Control
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
            {hasPermission(`pipeline:agents:usageStats:view`, agentInstance?.userAccess) && (
              <Tab
                value="usageStats"
                icon={<InsightsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'usageStats' ? 700 : 500 }}>
                    Usage Stats
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
            {hasPermission(`pipeline:agents:auditTrail:view`, agentInstance?.userAccess) && (
              <Tab
                value="auditTrail"
                icon={<InsightsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'auditTrail' ? 700 : 500 }}>
                    Audit Trail
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
          </Tabs>
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 1 }}>
        {isOpen && (
          <>
            {hasPermission(`pipeline:agents:accessControl:view`, agentInstance?.userAccess) && (
              <TabPanel value="accessControl" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <EntityAccessControl entity={agentInstance} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:agents:usageStats:view`, agentInstance?.userAccess) && (
              <TabPanel value="usageStats" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <AgentUsageStats agentInstance={agentInstance} maxHeight={maxHeight} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:agents:auditTrail:view`, agentInstance?.userAccess) && (
              <TabPanel value="auditTrail" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <AuditRecords entity={agentInstance} />
              </TabPanel>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AgentInstanceAdministration;
